/**
 * WERSJA „OBECNA” (produkcja 0.31.3) — „Uprawnienia i alerty z kartoteki TMS” na karcie pojazdu, kopia z `main`
 * (`git show origin/main:apps/app/src/admin/fleet/CarEligibilities.tsx`), 12.10.2026.
 *
 * Decyzja użytkownika 12.10.2026: „obecna” wersja aplikacji ma zachowywać
 * się dokładnie jak produkcja, a zmiany z develop są za przełącznikami.
 * Z tego pliku korzysta karta pojazdu przy wyłączonym przełączniku
 * `vehicleCardPrototype` (`fleet/CarShow.tsx`). Zmieniamy go tylko po to,
 * żeby wpiąć inne przełączniki (sekcja domyślnie niewidoczna) — przy
 * wszystkich wyłączonych ekran ma być taki jak na produkcji. Po decyzji
 * „wchodzi” albo „odrzucone” katalog `legacy/` znika razem z przełącznikiem.
 */
import { useRecordContext, RaRecord } from 'react-admin';
import { Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { SectionCard, fmtCalendarDate, fmtDate } from '../../../shared/Panel';
import { clientDayKey } from '../../../shared/dateTime';

/** Wpis z `GET /cars/:id` — surowo z kartoteki TMS (od 01.10.2026). */
export interface VehicleEligibilityRow {
  name: string;
  /** `Blokada` / `Alert` — klasyfikacja klienta. */
  type: string | null;
  /** `true` = ważne, `false` = nieważne (odpowiedź Marathonu 16.10.2026, pyt. 8). */
  status: boolean | null;
  validUpTo: string | null;
  importedAt: string | null;
}

/**
 * „ważne / nieważne / brak danych” — odpowiedź Marathonu 16.10.2026, pyt. 8:
 * `true` = ważne (termin później niż teraz), `false` = nieważne. Do 16.10
 * status stał dosłownie („tak / nie”), bo jego znaczenia nie znaliśmy. Bez
 * przedrostka „status:” (poprawa brzmienia 01.10.2026) — kolumna i tak
 * nazywa się „Status z TMS”.
 */
export const statusLabel = (s: boolean | null | undefined): string =>
  s === true ? 'ważne' : s === false ? 'nieważne' : 'brak danych';
// Poprawka obowiązkowa (fakt od klienta, 16.10.2026) — także w wersji
// „obecnej”, bez przełącznika: dosłowne „tak / nie” nic planiście nie mówiło.

/**
 * Czy termin-DATA jest wcześniejszy niż dziś (dzień polski). Jedyny fakt,
 * który tu oznaczamy: wpis z terminem przed dzisiaj nie obowiązuje, bez
 * względu na to, co znaczy jego status.
 */
export function expiredBeforeToday(iso: string | null | undefined, now: Date = new Date()): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return d.toISOString().slice(0, 10) < clientDayKey(now);
}

/** Typ to wartość z TMS klienta — pokazujemy ją, kolor tylko podpowiada. */
const TypeChip = ({ type }: { type: string | null }) => (
  <Chip
    size="small"
    variant="outlined"
    color={type === 'Blokada' ? 'error' : type === 'Alert' ? 'warning' : 'default'}
    label={type ?? 'bez typu'}
  />
);

/**
 * „Uprawnienia i alerty z kartoteki TMS” na karcie auta.
 *
 * Endpoint klienta działa od 01.10.2026 (wcześniej 500). Pokazujemy surowe
 * wartości: typ wg klasyfikacji klienta, status dosłownie, termin. Znaczenia
 * statusu nie znamy (pytanie do Marathonu), więc jedyne oznaczenie to termin
 * przed dzisiejszym dniem — „wygasło”. Podpowiedzi aut czytają tylko termin
 * i tylko ostrzegają.
 */
export const CarEligibilities = ({ now }: { now?: Date }) => {
  const r = useRecordContext<RaRecord & Record<string, unknown>>();
  if (!r) return null;
  const rows: VehicleEligibilityRow[] = Array.isArray(r.eligibilities)
    ? (r.eligibilities as VehicleEligibilityRow[])
    : [];
  const imported = rows
    .map((e) => e.importedAt)
    .filter((d): d is string => typeof d === 'string')
    .sort()
    .pop();
  return (
    <SectionCard title="Uprawnienia i alerty z kartoteki TMS">
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          TMS nie podaje uprawnień ani alertów dla tego pojazdu albo dane nie zostały jeszcze pobrane.
        </Typography>
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nazwa</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Status z TMS</TableCell>
                <TableCell>Ważne do</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((e) => {
                const expired = expiredBeforeToday(e.validUpTo, now);
                return (
                  <TableRow key={e.name}>
                    <TableCell>{e.name}</TableCell>
                    <TableCell>
                      <TypeChip type={e.type} />
                    </TableCell>
                    <TableCell>{statusLabel(e.status)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <span>{fmtCalendarDate(e.validUpTo)}</span>
                        {expired && <Chip size="small" color="error" label="wygasło" />}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Status z TMS: „ważne” albo „nieważne”. „Wygasło” oznacza termin wcześniejszy niż dzisiaj.
            {imported ? ` Pobrano z TMS: ${fmtDate(imported)}.` : ''}
          </Typography>
        </>
      )}
    </SectionCard>
  );
};
